import _extends from '@babel/runtime-corejs3/helpers/extends';
import React__default, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import styles from './Tooltip2.js';
import '../../utils/index.js';
import '../../utils/Popper/index.js';
import '../../utils/mergeRefs/index.js';
import '../../hooks/index.js';
import { useIsFocusVisible, conditionalA11yHelpersWrapper } from '../UIProvider/A11yHelpersContext.js';
import useControllableState from '../../hooks/useControllableState/useControllableState.js';
import useId from '../../hooks/useId/useId.js';
import useEventListener from '../../hooks/useEventListener/useEventListener.js';
import Popper from '../../utils/Popper/Popper.js';
import mergeRefs from '../../utils/mergeRefs/mergeRefs.js';
import { TOOLTIP_POPPER_OFFSET } from '../../utils/constants.js';

const FOCUSABLES_SELECTOR = 'button:not([tabindex="-1"]):not([aria-hidden="true"]), [href]:not([tabindex="-1"]):not([aria-hidden="true"]), input:not([tabindex="-1"]):not([aria-hidden="true"]), select:not([tabindex="-1"]):not([aria-hidden="true"]), textarea:not([tabindex="-1"]):not([aria-hidden="true"]), [tabindex]:not([tabindex="-1"]):not([aria-hidden="true"])';
const Tooltip = /*#__PURE__*/React__default.forwardRef((_ref, ref) => {
  var _ref2;
  let {
    content,
    unstable_contentKey: contentKey,
    placement = 'top',
    children,
    disabled = false,
    delayMs = 0,
    zIndex = 1000,
    className,
    portalTarget,
    isArrowRemoved = false,
    style = {},
    id,
    lightTheme = false,
    show: externalShow,
    onShowChange,
    showOnDescendantFocus = false,
    'aria-hidden': ariaHidden,
    ...rest
  } = _ref;
  const componentIsControlled = externalShow !== undefined;
  const [show, setShow] = useControllableState(externalShow, onShowChange, externalShow);
  const [isOpened, setIsOpened] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isMouseHoveringComponent, setIsMouseHoveringComponent] = useState(false);
  const anchorRef = useRef(null);
  const flyoutRef = useRef(null);
  const flyoutOffsetBackplateRef = useRef(null);
  const timer = useRef();
  const flyoutContainerId = useId(id);
  const {
    isFocusVisible
  } = useIsFocusVisible();
  const getIsAnchorKeyboardFocused = () => {
    var _anchorRef$current;
    return (showOnDescendantFocus ? (_anchorRef$current = anchorRef.current) === null || _anchorRef$current === void 0 ? void 0 : _anchorRef$current.contains(document.activeElement) : document.activeElement === anchorRef.current) && isFocusVisible;
  };
  const joinAriaDescribedBy = (ariaOldIds, ariaNewIds) => {
    const describedBy = new Set(ariaOldIds === null || ariaOldIds === void 0 ? void 0 : ariaOldIds.split(' '));
    describedBy.add(ariaNewIds);
    return Array.from(describedBy).join(' ');
  };
  const triggerShowTooltip = () => {
    const showTooltip = () => {
      setIsOpened(true);
      clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setIsVisible(true), delayMs);
    };
    if (!disabled) {
      if (!show) {
        setShow(true);
        if (componentIsControlled) {
          return;
        }
      }
      showTooltip();
    }
  };
  const triggerHideTooltip = () => {
    const hideTooltip = () => {
      clearTimeout(timer.current);
      setIsVisible(false);
      setIsOpened(false);
    };
    if (show) {
      setShow(false);
      if (componentIsControlled) {
        return;
      }
    }
    hideTooltip();
  };
  useEffect(() => {
    if (externalShow) {
      triggerShowTooltip();
    } else {
      triggerHideTooltip();
    }
  }, [externalShow]);
  const handleMouseEnter = () => {
    triggerShowTooltip();
    setIsMouseHoveringComponent(true);
  };
  const handleMouseLeave = event => {
    const isMouseEnteringAnchorOrTooltip = event.relatedTarget === anchorRef.current || event.relatedTarget === flyoutRef.current || event.relatedTarget === flyoutOffsetBackplateRef.current;

    /*
     * Prevents the tooltip from hiding when at least one of the following conditions are met:
     * A keyboard user exists and the anchor element is focused, but that same user is using a mouse to enter and leave the flyout or anchor, or
     * A user is moving the mouse between the anchor and the flyout
     */
    if (!isMouseEnteringAnchorOrTooltip) {
      setIsMouseHoveringComponent(false);
      if (!getIsAnchorKeyboardFocused()) {
        triggerHideTooltip();
      }
    }
  };
  const handleFocus = e => {
    // Prevents the tooltip from showing for mouse users when the tooltip's anchor element is programmatically focused
    if (isFocusVisible && (showOnDescendantFocus || e.target === e.currentTarget)) {
      triggerShowTooltip();
    }
  };
  const handleBlur = () => {
    /*
     * Prevents the tooltip from hiding when the cursor is hovering over the anchor or flyout.
     * This allows a user to keyboard focus the anchor and then highlight/select content inside of the flyout using a mouse.
     */
    if (!isMouseHoveringComponent) {
      triggerHideTooltip();
    }
  };
  const handleKeyDown = event => {
    if (event.key === 'Escape') {
      if (isVisible) {
        event.stopPropagation(); // Prevent event from closing modals or popovers
        event.preventDefault(); // Prevent Safari from exiting full-screen mode
      }
      triggerHideTooltip();
    }
  };
  useEffect(() => {
    if (disabled) {
      triggerHideTooltip();
    } else if (isMouseHoveringComponent || getIsAnchorKeyboardFocused()) {
      triggerShowTooltip();
    }
  },
  // the hide/show functionality of the tooltip based on hover and focus
  // are handled else where and thus don't need to be included here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [disabled]);
  useEffect(() => () => {
    clearTimeout(timer.current);
  }, []);
  useEffect(() => {
    if (showOnDescendantFocus && !ariaHidden) {
      var _anchorRef$current2;
      const focusableDescendants = (_anchorRef$current2 = anchorRef.current) === null || _anchorRef$current2 === void 0 ? void 0 : _anchorRef$current2.querySelectorAll(FOCUSABLES_SELECTOR);
      focusableDescendants === null || focusableDescendants === void 0 || focusableDescendants.forEach(elem => {
        const newAriaDescribedBy = joinAriaDescribedBy(elem.getAttribute('aria-describedby'), flyoutContainerId);
        elem.setAttribute('aria-describedby', newAriaDescribedBy);
      });
    }
  }, [anchorRef, ariaHidden, flyoutContainerId, showOnDescendantFocus]);

  // Allows the user to press the `Escape` key to close the flyout even if the anchor element is not focused
  // handle in capture so that it runs before modal or popover event handlers
  useEventListener(document, 'keydown', handleKeyDown, {
    isCaptureEvent: true,
    isDisabled: !isOpened
  });
  useEventListener(anchorRef, 'mouseenter', handleMouseEnter);
  useEventListener(anchorRef, 'mouseleave', handleMouseLeave);
  useEventListener(anchorRef, 'focusin', handleFocus);
  useEventListener(anchorRef, 'focusout', handleBlur);
  const commonFlyoutListenerOptions = {
    additionalDependencies: [isOpened, contentKey]
  };
  useEventListener(flyoutRef, 'mouseenter', handleMouseEnter, commonFlyoutListenerOptions);
  useEventListener(flyoutRef, 'mouseleave', handleMouseLeave, commonFlyoutListenerOptions);
  return /*#__PURE__*/React__default.createElement(Popper, {
    open: isOpened,
    alwaysInDOM: !disabled,
    placement: placement,
    anchor: /*#__PURE__*/React__default.cloneElement(children, {
      ref: mergeRefs(anchorRef, (_ref2 = children.ref) !== null && _ref2 !== void 0 ? _ref2 : null),
      'aria-describedby': !ariaHidden ? joinAriaDescribedBy(children.props['aria-describedby'], flyoutContainerId) : children.props['aria-describedby']
    }),
    popper: /*#__PURE__*/React__default.createElement("div", _extends({}, rest, {
      id: flyoutContainerId,
      key: contentKey,
      ref: mergeRefs(ref, flyoutRef),
      className: classNames(className, styles.tooltip, {
        [styles.visible]: isVisible,
        [styles.lightTheme]: lightTheme,
        [styles.srOnly]: !isOpened
      }),
      style: {
        zIndex,
        ...style
      },
      role: "tooltip",
      "aria-hidden": ariaHidden
    }), content, /*#__PURE__*/React__default.createElement("div", {
      "aria-hidden": true,
      className: styles.offsetBackplate,
      ref: flyoutOffsetBackplateRef
    })),
    modifiers: [{
      name: 'offset',
      options: {
        offset: TOOLTIP_POPPER_OFFSET
      }
    }, {
      name: 'flip',
      options: {
        flipVariations: false
      }
    }],
    arrow: isArrowRemoved ? undefined : /*#__PURE__*/React__default.createElement("div", {
      className: styles.arrow
    }, /*#__PURE__*/React__default.createElement("div", null)),
    portalTarget: portalTarget
  });
});
var Tooltip$1 = conditionalA11yHelpersWrapper(Tooltip);

export { Tooltip$1 as default };
